/* eslint-disable react-refresh/only-export-components --
 * Canonical React Context pattern — co-locates the
 * :func:`EntityDetailsSectionsProvider` component with its consumer
 * hook (:func:`useEntityDetailsSectionsContext`) + the
 * :type:`EntityDetailsSectionsContextValue` interface. Splitting the
 * hook into a separate file is the rule's recommendation but produces
 * an orphaned module that obscures the context's single-source-of-truth
 * nature (mirrors :file:`multi-pane/multi-pane-context.tsx`). Fast
 * refresh degrades to a full reload when this small, stable seam
 * changes — acceptable for infrastructure that changes rarely.
 */
/**
 * ``EntityDetailsSectionsContext`` — the per-panel seam that wires each
 * :func:`SectionShell` to its entity type's persisted expand/collapse
 * state without threading props through every section component.
 *
 * Each Entity Details panel (platform / aggregator / detection / alert)
 * provides this context **once** with the entity type + the
 * :func:`useSectionVisibility` result for that type. Every
 * ``SectionShell`` rendered inside then self-wires by its own ``id``:
 * it reads ``isExpanded(id)`` for its initial collapsed state and calls
 * ``setExpanded(id, …)`` on toggle. A shell rendered with no provider
 * in the tree (e.g. an isolated shell unit test) degrades to a plain,
 * non-collapsible header — the disclosure behavior is additive.
 *
 * Visibility gating stays in the parent panel
 * (``{isVisible(id) && <SectionShell …>}``) so hidden sections are
 * elided from the DOM entirely (header + body both, per ADR-024 §4) —
 * the context only carries the expand/collapse dimension that each
 * shell needs to drive its own disclosure.
 *
 * Cross-references:
 *   - REQ-UI-064 (collapsible sections, persisted, all entity types).
 *   - REQ-UI-092 (per-type expand/collapse defaults).
 *   - ADR-024 §4 (consolidated panel structure + section visibility).
 *   - ADR-033 §10 (relocated section config).
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type {
  EntityDetailType,
  UseSectionVisibilityResult,
} from "@/entity-details/use-section-visibility";

/** The value a panel publishes for its child section shells. */
export interface EntityDetailsSectionsContextValue {
  /**
   * Which entity type's section defaults are in scope, or **omitted** when the
   * provider is backed by something other than the persisted
   * ``EntityDetailsDefaults`` document.
   *
   * Optional because the three status kinds (``status-component`` /
   * ``status-interface`` / ``status-connection``) collapse **session-locally**
   * per REQ-UI-131 and have no ``EntityDetailsDefaults`` key to name — that
   * field is an additive Lane A api-change-request landing in Phase G. Widening
   * to a string would have let a typo through; omitting says what is true.
   * ``SectionShell`` never reads this — only the gear menu's owner does, and no
   * status surface renders a gear (ADR-024 §4).
   */
  entityType?: EntityDetailType;
  /** The bound :func:`useSectionVisibility` result for that type —
   *  the shells read ``isExpanded`` / ``setExpanded`` off it. */
  sections: UseSectionVisibilityResult;
}

const EntityDetailsSectionsContext = createContext<EntityDetailsSectionsContextValue | null>(null);

/**
 * Provide the section context to a panel's subtree. Render once per
 * panel, wrapping the section list.
 */
export function EntityDetailsSectionsProvider({
  value,
  children,
}: {
  value: EntityDetailsSectionsContextValue;
  children: ReactNode;
}): React.ReactElement {
  return (
    <EntityDetailsSectionsContext.Provider value={value}>
      {children}
    </EntityDetailsSectionsContext.Provider>
  );
}

/**
 * Read the section context, or ``null`` when no provider is present.
 * :func:`SectionShell` uses the ``null`` case to fall back to a
 * non-collapsible header (no provider ⇒ no persisted collapse state to
 * drive a disclosure).
 */
export function useEntityDetailsSectionsContext(): EntityDetailsSectionsContextValue | null {
  return useContext(EntityDetailsSectionsContext);
}
